/**
 * Galería de BaseTemplate desde /api/site-settings (admin).
 * Sin Worker ni portfolio: solo Cloudinary URLs curadas en site_settings.
 */

import { getGalleryPhotos } from "@/config/galleryConfig";
import type { SiteSettings } from "@/hooks/useSiteSettings";
import { isCloudinaryImage, isSameCloudinaryImage } from "@/lib/cloudinaryUtils";

export type BaseTemplateGalleryMediaItem = {
  id: number;
  type: "photo";
  src: string;
  title: string;
};

type GallerySettings = Pick<
  SiteSettings,
  | "galleryImageUrls"
  | "alternativeGalleryEnabled"
  | "alternativeGalleryUrls"
  | "logoUrl"
>;

function trimGalleryUrls(urls: string[]): string[] {
  return urls
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0)
    .map((u) => u.trim());
}

/** galleryImageUrls: solo Cloudinary (URLs o public_id). */
function cloudinaryGalleryUrls(urls: string[], logoUrl?: string): string[] {
  const trimmed = trimGalleryUrls(urls).filter((u) => isCloudinaryImage(u));
  if (!logoUrl?.trim()) return trimmed;
  return trimmed.filter((u) => !isSameCloudinaryImage(logoUrl, u));
}

/** alternativeGalleryUrls: cualquier URL válida (admin puede curar fuera de Cloudinary). */
function alternativeGalleryUrls(urls: string[], logoUrl?: string): string[] {
  const trimmed = trimGalleryUrls(urls);
  if (!logoUrl?.trim()) return trimmed;
  return trimmed.filter((u) => !isSameCloudinaryImage(logoUrl, u));
}

/**
 * Cache del orden barajado a nivel de módulo: la galería se baraja UNA sola vez
 * por set de URLs por carga de página. Persiste a través de re-renders, refetch de
 * site-settings (refetchOnWindowFocus) y navegación SPA (remontaje del template).
 * Solo se resetea con un refresh real (el módulo se reevalúa), que es justo cuando
 * el usuario espera ver fotos distintas.
 */
const shuffledOrderCache = new Map<string, string[]>();

function shuffleOncePerPageLoad(urls: string[]): string[] {
  // Clave por contenido del set (independiente del orden de entrada) para que el
  // mismo conjunto de fotos siempre reuse el mismo orden barajado en esta sesión.
  const key = [...urls].sort().join(" ");
  const cached = shuffledOrderCache.get(key);
  if (cached) return cached;
  const shuffled = [...urls].sort(() => Math.random() - 0.5);
  shuffledOrderCache.set(key, shuffled);
  return shuffled;
}

function toPhotoItems(
  urls: string[],
  shuffle: boolean,
): BaseTemplateGalleryMediaItem[] {
  const list = shuffle ? shuffleOncePerPageLoad(urls) : urls;
  return list.map((src, i) => ({
    id: i + 1,
    type: "photo" as const,
    src,
    title: "",
  }));
}

/** Prioridad: galería alternativa (orden fijo) → galleryImageUrls (shuffle opcional). */
export function buildBaseTemplateGalleryFromSiteSettings(
  settings: GallerySettings,
  options?: { shuffleGallery?: boolean },
): BaseTemplateGalleryMediaItem[] {
  const logoUrl =
    typeof settings.logoUrl === "string" && settings.logoUrl.trim()
      ? settings.logoUrl.trim()
      : undefined;

  if (settings.alternativeGalleryEnabled === true) {
    const alt = alternativeGalleryUrls(
      settings.alternativeGalleryUrls ?? [],
      logoUrl,
    );
    if (alt.length > 0) return toPhotoItems(alt, false);
  }

  const gallery = cloudinaryGalleryUrls(settings.galleryImageUrls ?? [], logoUrl);
  if (gallery.length > 0) {
    return toPhotoItems(gallery, options?.shuffleGallery ?? true);
  }

  return [];
}

type MediaPropItem = {
  id: number;
  type: "video" | "photo";
  src: string;
  title: string;
};

/** Fallback legacy: solo fotos Cloudinary del prop mediaItems (sin videos Mux). */
export function cloudinaryPhotosFromMediaItems(
  mediaItems: MediaPropItem[],
  logoUrl?: string,
): BaseTemplateGalleryMediaItem[] {
  const urls = mediaItems
    .filter((item) => item.type === "photo")
    .map((item) => item.src);
  return buildBaseTemplateGalleryFromSiteSettings(
    { galleryImageUrls: urls, logoUrl },
    { shuffleGallery: false },
  );
}

/**
 * Galería curada del home (BaseTemplate #portfolio).
 * Solo site-settings — nunca portfolio API ni mediaItems del padre.
 */
export function resolveHomeGalleryFromSiteSettings(
  settings: GallerySettings,
  options?: { shuffleGallery?: boolean },
): BaseTemplateGalleryMediaItem[] {
  return buildBaseTemplateGalleryFromSiteSettings(settings, options);
}

/** Indica si hay fotos curadas para mostrar en el bloque #portfolio del home. */
export function hasHomeGalleryFromSiteSettings(settings: GallerySettings): boolean {
  return resolveHomeGalleryFromSiteSettings(settings, { shuffleGallery: false }).length > 0;
}

/**
 * Fallback por entorno: VITE_GALLERY_PHOTOS (build-time). Es SOLO fallback —
 * site-settings (admin/DB) es la autoridad y tiene prioridad. La key existe para
 * que una marca sin galería curada en la DB pueda sembrar fotos desde el entorno.
 */
function galleryFromEnvFallback(): BaseTemplateGalleryMediaItem[] {
  const envItems = getGalleryPhotos();
  if (envItems.length === 0) return [];
  return envItems.map((item, i) => ({
    id: i + 1,
    type: "photo" as const,
    src: item.src.trim(),
    title: item.title ?? "",
  }));
}

/**
 * Prioridad de la galería del home (BaseTemplate/BaseTemplateBlack):
 *   1. Galería curada de site-settings (admin/DB) — autoridad.
 *   2. Fallback por entorno (VITE_GALLERY_PHOTOS) — solo si la DB no tiene galería.
 *   3. mediaItems prop (legacy páginas demo sin admin).
 *
 * Las env keys quedan como red de seguridad: todo lo gestiona el admin desde
 * /admin/site-settings y, en cuanto haya fotos curadas, ganan sobre el entorno.
 */
export function resolveBaseTemplateGalleryPhotos(
  settings: GallerySettings,
  mediaItems: MediaPropItem[],
  options?: { shuffleGallery?: boolean },
): BaseTemplateGalleryMediaItem[] {
  const fromSettings = resolveHomeGalleryFromSiteSettings(settings, options);
  if (fromSettings.length > 0) return fromSettings;

  const fromEnv = galleryFromEnvFallback();
  if (fromEnv.length > 0) return fromEnv;

  const fromProp = cloudinaryPhotosFromMediaItems(mediaItems, settings.logoUrl);
  if (fromProp.length === 0) return [];
  if (options?.shuffleGallery) {
    return [...fromProp].sort(() => Math.random() - 0.5);
  }
  return fromProp;
}